import {
  GetAnonVisitorsCapturedQuery,
  GetConversionMonitoringMetricsQuery,
  GetMetricsComparisonQuery,
  GetTotalEventsTrackedQuery,
  GetVisitorsRecognizedQuery,
  GetEmqScoreAverageAndTrendQuery
} from '@/gql/graphql';
import { DateTime } from 'luxon';

export const PURCHASE_CONVERSION_RATE_LABEL = 'Purchase Conversion Rate';
export const VISITORS_RECOGNIZED_LABEL = 'Visitors Recognized';
export const ANON_VISITORS_CAPTURED_LABEL = 'Anon Visitors Recognized';
// ALWAYS USE DATE AS THE X AXIS KEY REGARDLESS OF HOW THE DATA IS FORMATTED. OTHERWISE CHARTS WILL NOT RENDER CORRECTLY.
export const X_AXIS_KEY = 'date';

export type ChartOperation = {
  type: 'sum' | 'average';
  label: string;
};

export type TimeSeriesDataPoint = {
  [X_AXIS_KEY]: string;
  fullDate: string;
  [PURCHASE_CONVERSION_RATE_LABEL]?: number;
  [VISITORS_RECOGNIZED_LABEL]?: number;
  [ANON_VISITORS_CAPTURED_LABEL]?: number;
  [key: string]: number | string | undefined;
};

// Utility function to format dates into UTC short dates (e.g. Oct 20)
export const formatDate = (isoDate: string) => {
  const date = DateTime.fromISO(isoDate, { zone: 'UTC' });
  return {
    short: date.toLocaleString({ month: 'short', day: 'numeric' }),
    full: date.toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' })
  };
};

/**
 * Transform the metrics response to a time series data
 * @param metrics - The metrics response from Hermione via GraphQL
 * @returns - An array of objects with date and data keys as follows:
 * [
 *  { date: 'Oct 20', "Purchase Conversion Rate": 100% },
 *  { date: 'Oct 21', "Purchase Conversion Rate": 100% },
 *  { date: 'Oct 22', "Purchase Conversion Rate": 100% }
 * ]
 */
export const transformMetricToTimeSeries = ({
  metric,
  label,
  fromDate,
  toDate
}: {
  metric:
    | GetConversionMonitoringMetricsQuery['getConversionMonitoringMetrics']
    | GetVisitorsRecognizedQuery['getVisitorsRecognized']
    | GetAnonVisitorsCapturedQuery['getAnonVisitorsCaptured'];
  label: string;
  fromDate?: string;
  toDate?: string;
}): TimeSeriesDataPoint[] => {
  // Use provided dates if available, otherwise fall back to metric's dateRange
  const effectiveFromDate = fromDate ?? metric.dateRange?.fromDate;
  const effectiveToDate = toDate ?? metric.dateRange?.toDate;

  if (!effectiveFromDate || !effectiveToDate) {
    console.error('No date range found');
    return [];
  }

  // Create UTC DateTime objects
  let start: DateTime;
  let end: DateTime;
  try {
    start = DateTime.fromISO(effectiveFromDate, { zone: 'UTC' }).startOf('day');
    end = DateTime.fromISO(effectiveToDate, { zone: 'UTC' }).startOf('day');
    if (!start.isValid || !end.isValid) {
      console.error('Invalid date format');
      return [];
    }
  } catch (e) {
    console.error('Error parsing dates:', e);
    return [];
  }

  const expectedDays = end.diff(start, 'days').days + 1; // +1 because we want to include both start and end dates
  const timeSeriesData: TimeSeriesDataPoint[] = [];

  // Only take the expected number of days from the data array
  metric.data.slice(0, expectedDays).map((data, index) => {
    const currentDate = start.plus({ days: index });
    const dates = formatDate(currentDate.toISO() ?? '');

    timeSeriesData.push({
      [X_AXIS_KEY]: dates.short,
      fullDate: dates.full,
      [label]: data
    });
  });

  return timeSeriesData;
};

/**
 * Transform multiple metrics responses to time series data.
 * @param metrics - An array of metric responses.
 * @returns - An array of objects with date and data keys for all metrics.
 */
export const transformMultipleMetricsToTimeSeries = (
  metrics: GetTotalEventsTrackedQuery['getTotalEventsTracked'],
  fromDate?: string,
  toDate?: string
): TimeSeriesDataPoint[] => {
  if (metrics.length === 0) {
    console.error('No metrics provided');
    return [];
  }

  const dateMap: Record<string, TimeSeriesDataPoint> = {};

  // Calculate expected number of days
  const effectiveFromDate = fromDate ?? metrics[0].dateRange?.fromDate;
  const effectiveToDate = toDate ?? metrics[0].dateRange?.toDate;

  if (!effectiveFromDate || !effectiveToDate) {
    console.error(`Invalid metric or date range`);
    return [];
  }

  // Create UTC DateTime objects
  let start: DateTime;
  let end: DateTime;
  try {
    start = DateTime.fromISO(effectiveFromDate, { zone: 'UTC' }).startOf('day');
    end = DateTime.fromISO(effectiveToDate, { zone: 'UTC' }).startOf('day');
    if (!start.isValid || !end.isValid) {
      console.error('Invalid date format');
      return [];
    }
  } catch (e) {
    console.error('Error parsing dates:', e);
    return [];
  }

  const expectedDays = end.diff(start, 'days').days + 1; // +1 because we want to include both start and end dates

  for (const metric of metrics) {
    const label = metric.labels[0];

    // Only take the expected number of days from the data array
    metric.data.slice(0, expectedDays).forEach((value, index) => {
      const currentDate = start.plus({ days: index });
      const dates = formatDate(currentDate.toISO() ?? '');

      if (!(dates.short in dateMap)) {
        dateMap[dates.short] = {
          [X_AXIS_KEY]: dates.short,
          fullDate: dates.full
        };
      }

      dateMap[dates.short][label] = value;
    });
  }

  const timeSeriesData = Object.values(dateMap).sort((a, b) => {
    const dateA = DateTime.fromFormat(a.fullDate, 'MMM d, yyyy');
    const dateB = DateTime.fromFormat(b.fullDate, 'MMM d, yyyy');
    return dateA.isValid && dateB.isValid ? dateA.toMillis() - dateB.toMillis() : 0;
  });

  return timeSeriesData;
};

export const calculateInterval = (dataLength: number) => {
  if (dataLength <= 7) return 0;
  if (dataLength <= 14) return 1;
  return Math.floor(dataLength / 7);
};

export const calculateAverageForPurchaseConversionRate = (data: number[]) => {
  const average = data.reduce((acc: number, curr: number) => acc + curr, 0) / data.length;
  if (average > 1) {
    return `${average.toFixed(0)}%`;
  } else {
    return `${(average * 100).toFixed(0)}%`;
  }
};

export function getDefaultDateRange() {
  const today = DateTime.now().startOf('day');
  const lastWeek = today.minus({ days: 7 }).startOf('day');
  return { from: lastWeek.toJSDate(), to: today.toJSDate() };
}

export const formatNumber = (num: number): string => {
  // If the number is 1 million or more, display it in millions
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }

  // If the number is 1,000 or more, display it in thousands
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }

  return num.toString();
};

export function getRelativeTimeDescription(fromDate: string, toDate: string): string {
  const days = DateTime.fromISO(toDate).diff(DateTime.fromISO(fromDate), 'days').days;
  if (days === 0) {
    return 'previous day';
  } else {
    return `previous ${days} days`;
  }
}

export const getGrowthText = (comparison: GetMetricsComparisonQuery | undefined | null) => {
  try {
    if (
      !comparison?.getMetricsComparison ||
      typeof comparison.getMetricsComparison.percentageChange !== 'number'
    )
      return '-';
    const { percentageChange } = comparison.getMetricsComparison;
    return `${percentageChange >= 0 ? '+' : ''}${percentageChange.toFixed(1)}%`;
  } catch (error) {
    console.error('Error generating growth text:', error);
    return '-';
  }
};

export const getGrowthTrend = (
  comparison: GetMetricsComparisonQuery | undefined | null
): 'up' | 'down' => {
  try {
    if (
      !comparison?.getMetricsComparison ||
      typeof comparison.getMetricsComparison.percentageChange !== 'number'
    )
      return 'down';
    return comparison.getMetricsComparison.percentageChange >= 0 ? 'up' : 'down';
  } catch (error) {
    console.error('Error determining growth trend:', error);
    return 'down';
  }
};

export const getTotalEventsTrackedMetric = (
  inputMetrics: GetTotalEventsTrackedQuery['getTotalEventsTracked']
) => {
  // Find Contextual events by label
  const contextualEvents = inputMetrics.find((metric) =>
    metric.labels[0].includes('Contextual Server Events')
  );
  const pixelEvents = inputMetrics.filter(
    (metric) => !metric.labels[0].includes('Contextual Server Events')
  );

  // Only include pixel sources with non-zero data
  const activePixelEvents = pixelEvents.filter((metric) => metric.aggregate > 0);

  // Filter out pixel sources with all zero values for timeSeries
  const metrics = inputMetrics.filter((metric) => {
    // Always include Contextual events
    if (metric.labels[0].includes('Contextual Server Events')) {
      return true;
    }
    // For pixel events, check if they have any non-zero values
    return metric.data.some((value) => value > 0);
  });

  // If we have no active pixel events or no contextual events, show total contextual events
  if (activePixelEvents.length === 0 || !contextualEvents) {
    return {
      total: formatNumber(contextualEvents?.aggregate ?? 0),
      timeSeries: transformMultipleMetricsToTimeSeries(metrics)
    };
  }

  // Calculate ratio for each active pixel source
  const ratios = activePixelEvents.map((pixel) => {
    const contextual = contextualEvents.aggregate || 0;
    const pixel_events = pixel.aggregate || 0;
    // Calculate how much higher/lower contextual is compared to pixel
    return ((contextual - pixel_events) / pixel_events) * 100;
  });

  // Calculate average ratio
  const averageRatio = ratios.reduce((sum, ratio) => sum + ratio, 0) / ratios.length;

  // Flip the sign since we want positive to mean contextual is higher
  return {
    total: `${averageRatio >= 0 ? '+' : ''}${averageRatio.toFixed(1)}%`,
    timeSeries: transformMultipleMetricsToTimeSeries(metrics)
  };
};

export const getEMQScoreText = (
  data?: GetEmqScoreAverageAndTrendQuery['getEMQScoreAverageAndTrend']
) => {
  if (!data) {
    return '-';
  }

  if (!data.endScore || !data.priorScore || data.priorScore === 0) {
    return '0.00x';
  }

  let growth;
  if (data.endScore >= data.priorScore) {
    growth = Math.abs(data.endScore - data.priorScore) / data.priorScore;
  } else {
    growth = Math.abs(data.priorScore - data.endScore) / data.endScore;
  }

  return `${growth.toFixed(2)}x`;
};

export const getEMQGrowthTrend = (
  data?: GetEmqScoreAverageAndTrendQuery['getEMQScoreAverageAndTrend']
): 'up' | 'down' => {
  const delta = 0.1;
  if (!data) {
    return 'up';
  }

  if (!data.endScore || !data.priorScore) {
    return 'up';
  }

  if (!data.priorScore) {
    return 'up';
  }

  if (!data.endScore) {
    return 'up';
  }

  if (data.endScore + delta >= data.priorScore) {
    return 'up';
  }

  return 'down';
};

/**
 * Combine two time-series arrays (built via transformMetricToTimeSeries) by their date key.
 * Missing values are filled with 0.
 */
export const combineTwoSeriesByDate = (
  seriesA: TimeSeriesDataPoint[],
  seriesB: TimeSeriesDataPoint[],
  keyA: string,
  keyB: string
): TimeSeriesDataPoint[] => {
  const map: Record<string, TimeSeriesDataPoint> = {};

  for (const point of seriesA) {
    map[point.fullDate] = { ...point };
  }

  for (const point of seriesB) {
    const existing = map[point.fullDate] ?? {
      date: point.date,
      fullDate: point.fullDate,
      [keyA]: 0
    };
    existing[keyB] = point[keyB];
    map[point.fullDate] = existing;
  }

  // Ensure both keys exist on every row
  Object.values(map).forEach((row) => {
    row[keyA] = row[keyA] ?? 0;
    row[keyB] = row[keyB] ?? 0;
  });

  const timeSeriesData = Object.values(map).sort((a, b) => {
    const dateA = DateTime.fromFormat(a.fullDate, 'MMM d, yyyy');
    const dateB = DateTime.fromFormat(b.fullDate, 'MMM d, yyyy');
    return dateA.toMillis() - dateB.toMillis();
  });

  return timeSeriesData;
};
